import React, { useEffect, useState } from "react";
import {
  Search,
  Visibility,
  VisibilityOff,
  Apartment,
  EventNote,
} from "@mui/icons-material";
import Loading from "./Loading";
import { getAllBankDetails } from "./DataServices/BankDetailDataServices";

export interface BankDetail {
  id: string;
  listingId: string;
  receiverId: string;
  receiverName: string;
  receiverEmail: string;
  apartmentTitle: string;
  bankName: string;
  accountNumber: string;
  ifsCorRoutingCode: string;
  accountHolderName: string;
  submittedAt: string;
}

export const BankDetailsPage = () => {
  const [bankDetails, setBankDetails] = useState<BankDetail[]>([]);
  const [filteredBankDetails, setFilteredBankDetails] = useState<BankDetail[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleAccounts, setVisibleAccounts] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    const loadBankDetailsWithApartments = async () => {
      try {
        // Fetch bank details (now includes apartment title from backend)
        const data = await getAllBankDetails();
        const bankData = data as unknown as BankDetail[];
        setBankDetails(bankData);
        setFilteredBankDetails(bankData);
      } catch (error) {
        // Error caught, continue with loading state
      } finally {
        setIsLoading(false);
      }
    };

    loadBankDetailsWithApartments();
  }, []);

  useEffect(() => {
    const filtered = bankDetails.filter((detail) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (detail.apartmentTitle?.toLowerCase() ?? "").includes(searchLower) ||
        (detail.receiverName?.toLowerCase() ?? "").includes(searchLower) ||
        (detail.receiverEmail?.toLowerCase() ?? "").includes(searchLower) ||
        (detail.bankName?.toLowerCase() ?? "").includes(searchLower) ||
        (detail.accountHolderName?.toLowerCase() ?? "").includes(searchLower)
      );
    });
    setFilteredBankDetails(filtered);
  }, [searchTerm, bankDetails]);

  const toggleAccountVisibility = (detailId: string) => {
    const newVisible = new Set(visibleAccounts);
    if (newVisible.has(detailId)) {
      newVisible.delete(detailId);
    } else {
      newVisible.add(detailId);
    }
    setVisibleAccounts(newVisible);
  };

  const maskAccountNumber = (accountNumber?: string): string => {
    if (!accountNumber) return "N/A";
    if (accountNumber.length <= 4) return accountNumber;
    return "*".repeat(accountNumber.length - 4) + accountNumber.slice(-4);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="flex-1 bg-white overflow-hidden flex flex-col">
      <div className="flex-shrink-0 border-b border-gray-200 p-4 md:p-6 bg-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by apartment, name, email, bank, or account holder..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
              />
            </div>
          </div>
        </div>
        <div className="mt-3 text-sm text-gray-600">
          Found{" "}
          <span className="font-semibold text-gray-900">
            {filteredBankDetails.length}
          </span>{" "}
          bank detail records
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {filteredBankDetails.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-gray-500 text-lg mb-2">
                No bank details found
              </div>
              <div className="text-gray-400 text-sm">
                Bank details will appear here once they are submitted.
              </div>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 border-b border-gray-200">
                    Apartment Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 border-b border-gray-200">
                    Receiver
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 border-b border-gray-200">
                    Bank Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 border-b border-gray-200">
                    Account Number
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 border-b border-gray-200">
                    IFSC/Routing
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 border-b border-gray-200">
                    Account Holder
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 border-b border-gray-200">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredBankDetails.map((detail) => (
                  <tr
                    key={detail.id}
                    className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-start gap-2">
                        <Apartment className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                        <div className="font-medium text-gray-900">
                          {detail.apartmentTitle || "N/A"}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">
                        {detail.receiverName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {detail.receiverEmail}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {detail.bankName || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-gray-900">
                          {visibleAccounts.has(detail.id)
                            ? detail.accountNumber || "N/A"
                            : maskAccountNumber(detail.accountNumber)}
                        </span>
                        <button
                          onClick={() => toggleAccountVisibility(detail.id)}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                          title={
                            visibleAccounts.has(detail.id)
                              ? "Hide account number"
                              : "Show account number"
                          }
                        >
                          {visibleAccounts.has(detail.id) ? (
                            <Visibility className="w-4 h-4" />
                          ) : (
                            <VisibilityOff className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">
                      {detail.ifsCorRoutingCode || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {detail.accountHolderName || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <EventNote className="w-3 h-3 text-gray-400" />
                        {detail.submittedAt
                          ? new Date(detail.submittedAt).toLocaleDateString()
                          : "N/A"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankDetailsPage;
